import {
  getAlwaysVisibleFieldCount,
  getExpandedFieldCount,
} from "components/pages/schedule/helpers/workOrderCardHelper";
import { SIDE_BAR_LIMIT } from "config/appConfig";
import WorkOrder from "domain/WorkOrder";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import Authorization from "policies/Authorization";
import React, { useEffect } from "react";
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from "react-virtualized";
import styled from "styled-components";
import MoreResultsMessage from "./MoreResultsMessage";
import NoResultsFound from "./NoResultsFound";
import { transitionDefinition } from "./workOrderCards/helpers/workOrderCardAnimation";
import MockWorkOrderCardContainer from "./workOrderCards/MockWorkOrderCardContainer";
import WorkOrderCard from "./workOrderCards/WorkOrderCard";

const FULL_PAGE_INCLUDING_FOOTER = SIDE_BAR_LIMIT + 1;
const CARD_HEIGHT_INCLUDING_PADDING = 161;
const FIXED_ELEMENT_SIZES_WITH_PADDING = 80;
const cache = new CellMeasurerCache({
  fixedWidth: true,
  minHeight: FIXED_ELEMENT_SIZES_WITH_PADDING,
});

const Wrapper = styled.div`
  height: ${({ searchBarHeight }) => `calc(100% - ${searchBarHeight + 36}px)`};

  /*
   * This fixes a bug where the context menu gets cut off if there are only a few
   * work orders in the sidebar.  This appears to be an issue for several folks
   * out there but, unfortunately, the creators of react-virtualized-list don't
   * seem to care too much about fixing this.
   *
   * https://github.com/bvaughn/react-virtualized/issues/876
  */
  .ReactVirtualized__Grid__innerScrollContainer {
    overflow: visible !important;
  }
`;

export default function VirtualizedWorkOrderList({
  workOrders,
  onToggleExpandWorkOrder,
  expandedWorkOrders,
  layoutSettingData,
  ListRef,
}) {
  const applicationContext = useApplicationContext();
  const policy = Authorization.usePolicy();

  // biome-ignore lint/correctness/useExhaustiveDependencies: only care about workOrders changing
  useEffect(() => cache.clearAll(), [workOrders]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    if (ListRef.current) {
      ListRef.current.recomputeRowHeights();
      ListRef.current.forceUpdate();
    }
  }, [workOrders]);

  const rowHeight = ({ index }) => {
    const workOrder = workOrders[index];
    if (!workOrder) return 0;

    const hasLinePreferences = getHasLinePreferences(workOrder);

    const alwaysVisibleFieldCount = getAlwaysVisibleFieldCount(layoutSettingData.workOrderLayoutSetting);
    const expandableFieldCount = getExpandedFieldCount(layoutSettingData.workOrderLayoutSetting);

    const allFieldsCount = alwaysVisibleFieldCount + expandableFieldCount;
    const visibleFieldCount = expandedWorkOrders.includes(workOrder.id) ? allFieldsCount : alwaysVisibleFieldCount;
    const dividerCount = hasLinePreferences || workOrder.flag ? 2 : 1;
    const errorCount = (workOrder.standardProductionRate <= 0 ? 1 : 0) + (workOrder.quantity <= 0 ? 1 : 0);

    const dividersHeight = dividerCount * 17;
    const fieldsHeight = visibleFieldCount * 18;
    const linePrefHeight = hasLinePreferences ? 24 : 0;
    const flagsHeight = workOrder.flag ? 16 : 0;
    const errorFieldHeight = errorCount ? -18 + errorCount * 18 : 0;
    const flagsAndLinePrefHeight = Math.max(linePrefHeight, flagsHeight);
    const pillHeight = WorkOrder.cardStatus(workOrder) !== WorkOrder.SCHEDULED_STATUS_TYPES.UNDERSCHEDULED ? 4 : 0;

    return (
      FIXED_ELEMENT_SIZES_WITH_PADDING +
      dividersHeight +
      fieldsHeight +
      flagsAndLinePrefHeight +
      errorFieldHeight +
      pillHeight
    );
  };

  return (
    <Wrapper className="spec-side-bar" searchBarHeight={126}>
      <AutoSizer>
        {({ height, width }) => (
          <List
            deferredMeasurementCache={cache}
            estimatedRowSize={CARD_HEIGHT_INCLUDING_PADDING}
            height={height}
            noRowsRenderer={NoResultsFound}
            overscanRowCount={0}
            ref={ListRef}
            rowCount={getRowCount(workOrders)}
            rowHeight={rowHeight}
            rowRenderer={rowRenderer({
              applicationContext,
              expandedWorkOrders,
              ListRef,
              layoutSettingData,
              onToggleExpandWorkOrder,
              policy,
              workOrders,
            })}
            style={{ outline: "none" }}
            width={width}
          />
        )}
      </AutoSizer>
    </Wrapper>
  );
}

const getHasLinePreferences = (workOrder) =>
  workOrder.lineTypePreferencePrimary || workOrder.lineTypePreferenceSecondary;

function getRowCount(workOrders) {
  if (workOrders?.length === SIDE_BAR_LIMIT) {
    return FULL_PAGE_INCLUDING_FOOTER;
  }

  return workOrders?.length;
}

function rowRenderer({
  workOrders,
  applicationContext,
  policy,
  expandedWorkOrders,
  onToggleExpandWorkOrder,
  ListRef,
  layoutSettingData,
}) {
  return ({ index, style, isScrolling, parent, key }) => {
    const workOrder = workOrders[index];
    const atPageLimit = index === SIDE_BAR_LIMIT;

    if (atPageLimit) {
      return (
        <CellMeasurer cache={cache} key="-1" parent={parent} rowIndex={index}>
          {() => (
            <div key={key} style={{ ...style, paddingBottom: "4px" }}>
              <MoreResultsMessage />
            </div>
          )}
        </CellMeasurer>
      );
    }

    const hasPill = WorkOrder.cardStatus(workOrder) !== WorkOrder.SCHEDULED_STATUS_TYPES.UNDERSCHEDULED;

    const padding = hasPill ? "20px" : "16px";

    return (
      <CellMeasurer cache={cache} key={workOrder.id} parent={parent} rowIndex={index}>
        {() => (
          <div
            style={{
              ...style,
              paddingTop: padding,
              transition: `height ${transitionDefinition}, top ${transitionDefinition}`,
            }}
          >
            {isScrolling ? (
              <MockWorkOrderCardContainer
                applicationContext={applicationContext}
                expandedWorkOrders={expandedWorkOrders}
                layoutSettingData={layoutSettingData}
                policy={policy}
                workOrder={workOrder}
              />
            ) : (
              <WorkOrderCard
                expanded={expandedWorkOrders.includes(workOrder.id)}
                layoutSettingData={layoutSettingData}
                listRef={ListRef}
                onToggleExpandWorkOrder={onToggleExpandWorkOrder}
                workOrder={workOrder}
              />
            )}
          </div>
        )}
      </CellMeasurer>
    );
  };
}
